import { useCallback, useMemo, useState } from 'react';
import { Stack, Heading, Icon, Box, Text, Flex } from '@chakra-ui/react';
import Wizard from '../Wizard';
import { ListingModel } from 'models';
import { ListingGrid } from 'components';
import { ListingGridSelectionType } from 'components/Listing/ListingGrid/ListingGrid';
import { AiFillCheckCircle } from "react-icons/ai";
import { MdWarning, MdFilter, MdCropOriginal } from 'react-icons/md';
import { JSONObject } from 'models/Api/types';
import BulkTagCard from 'components/Card/BulkTagCard/BulkTagCard';
import OriginalMarkingWarning from '../../Alert/OriginalMarkingWarning';

export type ListingTagType = 'FAKE' | 'ORIGINAL' | 'SET';

interface ListingTagWizardProps {
  listings: ListingModel[];
  isLoading?: boolean;
  onSubmit?: (listings: ListingModel[], tagType: ListingTagType, data: JSONObject) => void;
  preselectedListings?: ListingModel[];
  initialTagType?: ListingTagType;
}

export default function ListingTagWizard({
  listings,
  isLoading = false,
  onSubmit = () => { },
  preselectedListings = [],
  initialTagType
}: ListingTagWizardProps) {
  const [selectedTagType, setSelectedTagType] = useState<ListingTagType | ''>(initialTagType || '');
  const [selectedListings, setSelectedListings] = useState<ListingModel[]>(preselectedListings);

  const isSingleListingMode = preselectedListings.length > 0;

  const onWizardSubmit = () => {
    let data = {};
    switch (selectedTagType) {
      case 'ORIGINAL':
        data = { is_unique: true };
        break;
      case 'SET':
        data = { multiple_items: true };
        break;
      case 'FAKE':
        data = { authentic: false };
        break;
      default:
        break;
    }
    if (selectedTagType && selectedListings.length > 0) {
      onSubmit(selectedListings, selectedTagType, data);
    }
  }

  const getConfirmationStepHeader = () => {
    switch (selectedTagType) {
      case 'ORIGINAL':
        return 'Mark Selected Listings as Original (Non-Editioned)';
      case 'SET':
        return 'Mark Selected Listings as Sets (Multiple Items)';
      case 'FAKE':
        return 'Mark Selected Listings as Fake';
      default:
        return '';
    }
  }

  const getTagDescription = () => {
    switch (selectedTagType) {
      case 'ORIGINAL':
        return 'These listings will be marked as containing original, non-editioned items.';
      case 'SET':
        return 'These listings will be marked as containing multiple items (sets).';
      case 'FAKE':
        return 'These listings will be marked as containing fake or counterfeit items.';
      default:
        return '';
    }
  }

  const getTagIcon = () => {
    switch (selectedTagType) {
      case 'ORIGINAL':
        return { icon: MdCropOriginal, color: 'pink.500' };
      case 'SET':
        return { icon: MdFilter, color: 'teal.500' };
      case 'FAKE':
        return { icon: MdWarning, color: 'red.500' };
      default:
        return { icon: MdCropOriginal, color: 'gray.500' };
    }
  };

  const handleListingSelection = useCallback((listing: ListingModel) => {
    setSelectedListings((previous) => {
      const exists = previous.some((selected) => selected.id === listing.id);
      if (exists) {
        return previous.filter((selected) => selected.id !== listing.id);
      }
      return [...previous, listing];
    });
  }, []);

  function renderTagTypeSelection() {
    return (
      <Stack spacing={6} alignItems="center">
        <Flex
          direction={{ base: 'column', md: 'row' }}
          gap={6}
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <BulkTagCard
            icon={MdCropOriginal}
            label="Original"
            tagColorScheme="pink"
            selected={selectedTagType === 'ORIGINAL'}
            onClick={() => setSelectedTagType('ORIGINAL')}
          />
          <BulkTagCard
            icon={MdFilter}
            label="Set"
            tagColorScheme="teal"
            selected={selectedTagType === 'SET'}
            onClick={() => setSelectedTagType('SET')}
          />
          <BulkTagCard
            icon={MdWarning}
            label="Fake"
            tagColorScheme="red"
            selected={selectedTagType === 'FAKE'}
            onClick={() => setSelectedTagType('FAKE')}
          />
        </Flex>
      </Stack>
    );
  }

  function renderListingSelection() {
    return (
      <Stack spacing={4}>
        <ListingGrid
          gridListings={listings}
          isLoading={false}
          showFilters={true}
          gridSelection={ListingGridSelectionType.MULTI}
          defaultSelectedListings={selectedListings}
          onListingCardClick={handleListingSelection}
          showAssociatedItems={false}
          showAssociationAction={false}
        />
      </Stack>
    );
  }

  function renderConfirmation() {
    const { icon, color } = getTagIcon();
    return (
      <Stack spacing={4}>
        <Stack spacing={2}>
          <Flex justifyContent="center" alignItems="center" gap={4} textAlign="left">
            <Icon as={icon} w={8} h={8} color={color} />
            <Stack spacing={1} alignItems="flex-start">
              <Heading fontSize="sm">{getConfirmationStepHeader()}</Heading>
              <Text fontSize="xs" color="gray.600">{getTagDescription()}</Text>
            </Stack>
          </Flex>
          <OriginalMarkingWarning listings={selectedListings} suggestionType={selectedTagType as any} />
          <Box maxHeight="400px" overflow="auto">
            <ListingGrid
              gridListings={selectedListings}
              isLoading={false}
              showFilters={false}
              gridSelection={ListingGridSelectionType.NONE}
              showAssociatedItems={false}
              showAssociationAction={false}
            />
          </Box>
        </Stack>
      </Stack>
    );
  }

  function renderWizardFinishStep() {
    return (
      <Stack gap={4} alignItems={'center'}>
        <Icon as={AiFillCheckCircle} color={'green.500'} w={10} h={10} />
        <Heading size={'sm'}>
          {selectedListings.length} Listing(s) Tagged Successfully
        </Heading>
        <Text fontSize="sm" color="gray.600" textAlign="center">
          Your changes have been applied to the selected listings.
        </Text>
      </Stack>
    );
  }

  const wizardSteps = useMemo(() => {
    const steps = [];

    // Only show tag selection step if no initial tag type provided
    if (!initialTagType) {
      steps.push({
        label: 'Select Tag Type',
        content: renderTagTypeSelection(),
        requiresValidation: true,
        isValidated: selectedTagType !== ''
      });
    }

    // Only show listing selection step if not in single listing mode
    if (!isSingleListingMode) {
      steps.push({
        label: 'Select Listings',
        content: renderListingSelection(),
        requiresValidation: true,
        isValidated: selectedListings.length > 0
      });
    }

    steps.push({
      label: 'Confirm',
      content: renderConfirmation(),
      requiresValidation: false,
      isValidated: true
    });

    return steps;
  }, [selectedTagType, selectedListings, listings, isSingleListingMode, initialTagType]);

  return (
    <Wizard
      steps={wizardSteps}
      finishStep={renderWizardFinishStep()}
      onSubmit={onWizardSubmit}
      loading={isLoading}
      showStepper
    />
  );
}
